import { Card, Skeleton, Image, Spin } from 'antd';
import { useOne } from 'app/definitions/hooks/data/useOne';
import { IQMASDocument } from 'app/definitions/interfaces/Document';
import { QMASLazyDocument, QMASLazyDocumentProps } from './QMASLazyDocument';
import React from 'react';
import { GetOneResponse } from 'app/context/data/types';
import { DOCUMENT_FILE_TYPE_ICON } from '../constants';
import classes from './DocumentItem.module.scss';
import Helper from 'app/modules/ui/helper';

type QMASCardDocumentProps = Partial<QMASLazyDocumentProps>;

const Content = (props: QMASCardDocumentProps) => {
  const { alt, documentId, skeleton, width = 65, height = 65 } = props;

  const { data, isLoading, isFetching } = useOne<GetOneResponse<IQMASDocument>>(
    {
      resource: 'document-list',
      id: documentId
    }
  );

  const {
    mineType,
    url: src,
    fileName,
    fileSize
  } = (data?.data ?? {}) as IQMASDocument;

  const Document = React.useMemo(() => {
    if (!mineType) return <div>Unsupported MIME type</div>;
    switch (mineType) {
      case 'image/jpeg':
      case 'image/png':
      case 'image/gif':
      case 'image/webp':
        return (
          <Image
            sizes={'large'}
            src={src}
            alt={alt}
            width={width}
            preview={false}
          />
        );
      case 'video/mp4':
      case 'video/webm':
        return <video src={src} height={height} controls />;
      case 'application/pdf':
        return <>{DOCUMENT_FILE_TYPE_ICON.pdf}</>;
      case 'application/msword':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return <>{DOCUMENT_FILE_TYPE_ICON.word}</>;
      case 'application/vnd.ms-excel':
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return <>{DOCUMENT_FILE_TYPE_ICON.excel}</>;
      default:
        return <div>Unsupported MIME type</div>;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const renderLoading = () => {
    if (skeleton) {
      return (
        <Skeleton
          active
          avatar={{ shape: 'square', size: 'large' }}
          paragraph={false}
          title={false}
          style={{ height, width: height }}
        ></Skeleton>
      );
    }
    return <Spin size="small" />;
  };

  return (
    <a
      className={classes.documentItem}
      style={{
        borderRadius: '4px',
        display: 'inline-block'
      }}
      href={src || '#'}
      target="_blank"
      rel="noreferrer"
    >
      <Card className={classes.card} bordered={false} size="small">
        <Card.Meta
          avatar={<>{isLoading || isFetching ? renderLoading() : Document}</>}
          title={fileName}
          description={fileSize ? Helper.file.formatFileSize(fileSize) : ''}
        />
      </Card>
    </a>
  );
};

export const QMASCardLazyDocument = (props: QMASCardDocumentProps) => {
  return (
    <QMASLazyDocument
      {...(props as any)}
      content={<Content {...props} />}
      placeholder={<Skeleton active avatar paragraph={false} title />}
    />
  );
};
